<template>
  <o-dialog
    ref="dialog"
    :title="$t('order.addCharge')"
    :info="$t('components.order.actions.addCharge.info')"
    :options="[
      { text: $t('general.cancel'), type: 'cancel' },
      { text: $t('components.order.actions.addCharge.confirm'), type: 'confirm', callback: confirm },
    ]"
    :loading="loading"
    :portal="portal"
    :valid="validate"
  >
    <div>
      <o-field-group
        :label="$t('components.order.actions.addCharge.charge')"
        :name="$t('components.order.actions.addCharge.charge')"
        class="mb-4 w-full"
      >
        <div class="flex items-center">
          <charge-select v-if="charges.length" v-model="charge" class="flex-grow" />

          <UButton icon="lucide:plus" class="h-11" :class="charges.length ? 'ml-2' : 'w-full'" @click="handleCreate">
            <p v-if="!charges.length" class="ml-3">
              {{ $t('components.order.actions.addCharge.newCharge') }}
            </p>
          </UButton>
        </div>
      </o-field-group>

      <div v-if="charge" class="mt-6 pt-4 text-left ">
        <o-form ref="form">
          <o-field-group
            v-slot="{ status }"
            :label="$t('general.price')"
            :name="$t('general.price')"
            class="mb-6 block"
            :rules="isAmountTypePercentage ? 'required|between:0,100' : 'required|currency'"
            :disabled="isLocked"
          >
            <div class="flex items-center space-x-2">
              <currency-field v-if="isAmountTypeFixed" v-model="_price" :status="status" :disabled="isLocked" class="w-1/2" />
              <number-field
                v-if="isAmountTypePercentage"
                v-model="_price"
                :min="0"
                :max="100"
                slim
                :status="status"
                trailing="%"
                class="w-1/2"
                :disabled="isLocked"
              />
              <segment-field v-model="charge.amountType" :options="chargeAmountTypeOptions" :disabled="isLocked" class="border w-1/2" />
            </div>
          </o-field-group>

          <o-field-group v-if="$features.get('loyalty')" :label="$t('general.loyaltyEnabled')" :info="$t('general.loyaltyEnabledInfo')" class="w-full mb-6 block">
            <toggle-field v-model="charge.loyaltyEnabled" />
          </o-field-group>

          <tax-select v-model="charge.taxIDs" class="w-full mb-2" />
        </o-form>
      </div>
    </div>

    <edit-dialog
      ref="editDialog"
      @success="handleSync"
    />
  </o-dialog>
</template>

<script setup lang="ts">
import { ChargeAmountType, ChargeAmountTypeLabels } from '@/_api/models/chargeModel'

import ChargeSelect from '@/components/chargeModel/elements/Select.vue'
import TaxSelect from '@/components/tax/elements/Select.vue'
import EditDialog from '@/components/chargeModel/actions/Edit.vue'
import {
  NumberField,
  ToggleField,
  SegmentField,
  CurrencyField,
} from '@/components/fields'

import { useChargeModelStore } from '~/stores/useChargeModelStore'

interface Props {
  order: Record<string, any>
  portal?: string
  loading?: boolean
}

interface Emits {
  (e: 'update:loading', value: boolean): void
  (e: 'update:order', value: any): void
}

const props = withDefaults(defineProps<Props>(), {
  portal: undefined,
  loading: false,
})

const emit = defineEmits<Emits>()

const { $api, $notification } = useNuxtApp()
const { t } = useI18n()

const chargeModelStore = useChargeModelStore()

const dialog = ref<any>()
const form = ref<any>()
const editDialog = ref<any>()

const charge = ref<any>(null)
const charges = computed(() => chargeModelStore.all)

const _price = computed({
  get () {
    return charge.value?.price
  },
  set (val: any) {
    charge.value.price = Number(val)
  },
})

const isLocked = computed(() => charge.value?.locked)

const isAmountTypeFixed = computed(() => charge.value?.isAmountTypeFixed)

const isAmountTypePercentage = computed(() => charge.value?.isAmountTypePercentage)

const chargeAmountTypeOptions = computed(() => {
  return Object.values(ChargeAmountType)
    .filter((v): v is ChargeAmountType => typeof v === 'number')
    .map((value) => ({
      label: t(ChargeAmountTypeLabels[value]),
      value,
    }))
})

async function open () {
  charge.value = null
  if (!charges.value.length) {
    await chargeModelStore.sync({ force: true })
  }
  dialog.value.open()
}

async function confirm () {
  emit('update:loading', true)

  try {
    const chargeVal = charge.value
    const response = await $api.order.addCharge(String(props.order.idOrder), chargeVal)
    const order = response.data

    emit('update:order', order)

    $notification.toast({
      title: t('components.order.actions.addCharge.success'),
      text: t('components.order.actions.addCharge.successInfo'),
      variant: 'success',
    })

    emit('update:loading', false)

    return true
  } catch (e) {
    console.log(e)
  }

  emit('update:loading', false)
}

function validate () {
  return charge.value && form.value?.validate()
}

function handleCreate () {
  editDialog.value.open()
}

function handleSync (chargeVal: any) {
  charge.value = chargeVal
  chargeModelStore.sync({ force: true })
}

defineExpose({ open })
</script>
